import { Menu, MoreVert } from '@mui/icons-material'
import { Typography, Box } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useMatches, useParams } from 'react-router-dom'
import { Uid } from '@/pages/utils/formatter'
import theme, {
  layoutStyles,
  closedDrawerWidth,
  drawerWidth,
} from '@/utils/theme'
import Sidebar from './Sidebar'
import { useMediaQuery } from '@mui/system'

const UidTitle = () => {
  const { uid } = useParams<{ uid: string }>()
  return uid ? <Uid value={uid} /> : <></>
}

const Layout = () => {
  const { t } = useTranslation()
  const matches = useMatches()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile)
  const [sidebarMinimized, setSidebarMinimized] = useState(false)
  const effectiveSidebarOpen = sidebarOpen && !sidebarMinimized

  const pageTitle = useMemo(() => {
    for (let i = matches.length - 1; i >= 0; i--) {
      const match = matches[i]
      if (match?.handle) {
        if (match?.handle?.isUidTitle) return <UidTitle />
        if (match?.handle?.title) return t(match.handle.title)
      }
    }
    return t('app.defaultTitle', 'Dashboard')
  }, [matches, t])

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen)

  useEffect(() => {
    if (isMobile) setSidebarOpen(false)
  }, [isMobile])

  const mainPanelStyle = {
    ...layoutStyles.mainPanel,
    width: {
      xs: '100%',
      md: `calc(100% - ${
        effectiveSidebarOpen ? drawerWidth : closedDrawerWidth
      }px)`,
    },
    transition: theme.transitions.create(['width', 'margin'], {
      duration: 300,
      easing: 'ease',
    }),
    ml: {
      md: effectiveSidebarOpen ? `${drawerWidth}px` : `${closedDrawerWidth}px`,
    },
  }

  return (
    <Box sx={layoutStyles.wrapper}>
      <Sidebar
        open={sidebarOpen}
        minimized={sidebarMinimized}
        onClose={toggleSidebar}
        onMinimize={() => setSidebarMinimized(!sidebarMinimized)}
      />

      <Box sx={mainPanelStyle}>
        <Box
          sx={{
            ...layoutStyles.toolbar,
            pl: '30px',
            backgroundColor: 'transparent',
          }}
        >
          <Box
            onClick={toggleSidebar}
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: 'white',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mr: 2,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              color: '#777',
              '&:hover': { backgroundColor: '#f5f5f5' },
              zIndex: theme.zIndex.drawer + 5,
            }}
          >
            {sidebarOpen ? (
              <MoreVert fontSize="small" />
            ) : (
              <Menu fontSize="small" />
            )}
          </Box>

          <Typography noWrap sx={layoutStyles.title}>
            {pageTitle}
          </Typography>
        </Box>

        <Box sx={layoutStyles.content}>
          <Box sx={layoutStyles.container}>
            <Outlet />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default Layout
